import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { badgeClass, dangerButtonClass, Field, FormActions, formatNumber, getApiErrorMessage, inputClass, inputErrorClass, primaryButtonClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { qualityService } from '../../services/quality.service';
import { cn } from '../../lib/utils';
import { clean, formatDate, isNumber, labelOf, numberOrNull, openButtonClass, ProductChoice, ProductPicker, TableFooter, thClass, today, useListState } from './partApprovalShared';

type Status = 'draft' | 'approved' | 'approved_with_deviation' | 'rejected';
const REASONS = ['new_part', 'design_change', 'process_change', 'tooling_change', 'new_source', 'production_gap'];
const REASON_LABEL: Record<string, string> = { new_part: 'New part', design_change: 'Design change', process_change: 'Process change', tooling_change: 'Tooling change', new_source: 'New source', production_gap: 'After a gap in production' };
const STATUS: Record<Status, { label: string; className: string }> = {
  draft: { label: 'Not decided', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  approved: { label: 'Approved', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  approved_with_deviation: { label: 'Approved with deviation', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  rejected: { label: 'Rejected', className: 'bg-red-50 text-red-700 ring-red-600/20' },
};

interface FaiRow {
  id: string;
  faiNumber: string;
  productId: string;
  productName: string;
  sku: string;
  drawingNumber: string | null;
  revision: string | null;
  reason: string;
  workOrderNumber: string | null;
  inspectedOn: string;
  inspectedByName: string | null;
  status: Status;
  notes: string | null;
  decisionNote: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  lines: number;
  checked: number;
  failed: number;
}
interface FaiLine {
  id: string;
  sequence: number;
  characteristic: string;
  type: 'measurement' | 'attribute';
  unit: string | null;
  nominal: number | null;
  lowerLimit: number | null;
  upperLimit: number | null;
  measuredValue: number | null;
  passed: boolean | null;
  instrumentId: string | null;
  instrumentCode: string | null;
  remarks: string | null;
}
interface Fai extends FaiRow {
  characteristics: FaiLine[];
}
interface LineState {
  characteristic: string;
  type: 'measurement' | 'attribute';
  unit: string;
  nominal: string;
  lowerLimit: string;
  upperLimit: string;
  measuredValue: string;
  passed: '' | 'pass' | 'fail';
  instrumentId: string;
}
const emptyLine = (): LineState => ({ characteristic: '', type: 'measurement', unit: '', nominal: '', lowerLimit: '', upperLimit: '', measuredValue: '', passed: '', instrumentId: '' });
const text = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value));
const limitsOf = (line: { type: string; unit: string | null; lowerLimit: number | null; upperLimit: number | null; nominal: number | null }) => {
  if (line.type === 'attribute') return 'Pass / fail';
  const unit = line.unit ? ` ${line.unit}` : '';
  if (line.lowerLimit !== null && line.upperLimit !== null) return `${formatNumber(line.lowerLimit, 5)} to ${formatNumber(line.upperLimit, 5)}${unit}`;
  if (line.lowerLimit !== null) return `At least ${formatNumber(line.lowerLimit, 5)}${unit}`;
  if (line.upperLimit !== null) return `At most ${formatNumber(line.upperLimit, 5)}${unit}`;
  return line.nominal !== null ? `${formatNumber(line.nominal, 5)}${unit}, no limits` : 'No limits';
};

const faiApi = {
  page: async (params: object): Promise<{ data: FaiRow[]; total: number; totalPages: number }> => (await api.get('/first-articles', { params: clean(params) })).data,
  one: async (id: string): Promise<Fai> => (await api.get(`/first-articles/${id}`)).data,
  save: async (id: string | null, data: object): Promise<Fai> => (id ? await api.put(`/first-articles/${id}`, data) : await api.post('/first-articles', data)).data,
  act: async (id: string, action: 'approve' | 'reject', data: object): Promise<Fai> => (await api.post(`/first-articles/${id}/${action}`, data)).data,
  remove: async (id: string): Promise<void> => {
    await api.delete(`/first-articles/${id}`);
  },
};

// ---------------------------------------------------------------- the report: every characteristic of the drawing

const FaiForm: React.FC<{ fai: Fai | null; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ fai, isPending, onCancel, onSubmit }) => {
  const { data: instruments = [] } = useQuery({ queryKey: ['quality', 'usable-instruments'], queryFn: () => qualityService.getUsableInstruments() });
  const [product, setProduct] = useState<ProductChoice | null>(fai ? { id: fai.productId, name: fai.productName, sku: fai.sku } : null);
  const [form, setForm] = useState({ reason: fai?.reason ?? 'new_part', drawingNumber: fai?.drawingNumber ?? '', revision: fai?.revision ?? '', inspectedOn: fai?.inspectedOn ?? today(), inspectedByName: fai?.inspectedByName ?? '', notes: fai?.notes ?? '' });
  const [lines, setLines] = useState<LineState[]>(() =>
    fai
      ? fai.characteristics.map((line) => ({ characteristic: line.characteristic, type: line.type, unit: line.unit ?? '', nominal: text(line.nominal), lowerLimit: text(line.lowerLimit), upperLimit: text(line.upperLimit), measuredValue: text(line.measuredValue), passed: line.passed === null ? '' : line.passed ? 'pass' : 'fail', instrumentId: line.instrumentId ?? '' }))
      : [emptyLine()],
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors({});
  };
  const setLine = (index: number, patch: Partial<LineState>) => {
    setLines((prev) => prev.map((line, position) => (position === index ? { ...line, ...patch } : line)));
    setErrors({});
  };
  // The checks of the incoming inspection plan of the part, as a starting point
  const copyPlan = useMutation({
    mutationFn: () => qualityService.getPlan(product?.id as string),
    onSuccess: (plan) => {
      if (plan.parameters.length === 0) return void toast.error('This part has no checks on its inspection plan');
      setLines(plan.parameters.map((parameter) => ({ ...emptyLine(), characteristic: parameter.name, type: parameter.type, unit: parameter.unit ?? '', nominal: text(parameter.nominal), lowerLimit: text(parameter.lowerLimit), upperLimit: text(parameter.upperLimit) })));
      toast.success(`${plan.parameters.length} characteristic(s) copied from the inspection plan`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'The inspection plan could not be read')),
  });
  // Whether the value is within its limits, when both are known
  const within = (line: LineState): boolean | null => {
    if (line.type !== 'measurement' || line.measuredValue.trim() === '' || !Number.isFinite(Number(line.measuredValue))) return null;
    if (line.lowerLimit.trim() === '' && line.upperLimit.trim() === '') return null;
    const measured = Number(line.measuredValue);
    return (line.lowerLimit.trim() === '' || measured >= Number(line.lowerLimit)) && (line.upperLimit.trim() === '' || measured <= Number(line.upperLimit));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!product) next.product = 'Choose the part';
    if (!form.inspectedOn) next.inspectedOn = 'Enter the date';
    else if (form.inspectedOn > today()) next.inspectedOn = 'Not in the future';
    if (lines.length === 0) next.lines = 'Add at least one characteristic';
    lines.forEach((line, index) => {
      if (line.characteristic.trim().length < 2) next[`line-${index}`] = 'Name the characteristic';
      else if (![line.nominal, line.lowerLimit, line.upperLimit, line.measuredValue].every(isNumber)) next[`line-${index}`] = 'Enter numbers';
      else if (line.lowerLimit.trim() !== '' && line.upperLimit.trim() !== '' && Number(line.lowerLimit) > Number(line.upperLimit)) next[`line-${index}`] = 'The lower limit is above the upper limit';
    });
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({
      productId: product?.id,
      reason: form.reason,
      drawingNumber: form.drawingNumber.trim() || null,
      revision: form.revision.trim() || null,
      inspectedOn: form.inspectedOn,
      inspectedByName: form.inspectedByName.trim() || null,
      notes: form.notes.trim() || null,
      lines: lines.map((line, index) => ({
        sequence: index + 1,
        characteristic: line.characteristic.trim(),
        type: line.type,
        unit: line.unit.trim() || null,
        nominal: numberOrNull(line.nominal),
        lowerLimit: line.type === 'measurement' ? numberOrNull(line.lowerLimit) : null,
        upperLimit: line.type === 'measurement' ? numberOrNull(line.upperLimit) : null,
        measuredValue: line.type === 'measurement' ? numberOrNull(line.measuredValue) : null,
        // A measurement with limits is judged by them
        passed: within(line) !== null ? null : line.passed === '' ? null : line.passed === 'pass',
        instrumentId: line.instrumentId || null,
      })),
    });
  };
  const small = 'py-1 text-right tabular-nums';

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Part" htmlFor="fa-product" required error={errors.product} className="sm:col-span-2">
          <ProductPicker id="fa-product" value={product} onChange={setProduct} disabled={!!fai} error={!!errors.product} />
        </Field>
        <Field label="Why a first article" htmlFor="fa-reason" required>
          <select id="fa-reason" value={form.reason} onChange={set('reason')} className={inputClass}>
            {REASONS.map((reason) => (<option key={reason} value={reason}>{REASON_LABEL[reason]}</option>))}
          </select>
        </Field>
        <Field label="Drawing number" htmlFor="fa-drawing"><input id="fa-drawing" type="text" maxLength={60} value={form.drawingNumber} onChange={set('drawingNumber')} className={inputClass} /></Field>
        <Field label="Revision" htmlFor="fa-revision"><input id="fa-revision" type="text" maxLength={30} value={form.revision} onChange={set('revision')} className={inputClass} /></Field>
        <Field label="Inspected on" htmlFor="fa-date" required error={errors.inspectedOn}><input id="fa-date" type="date" max={today()} value={form.inspectedOn} onChange={set('inspectedOn')} className={cn(inputClass, errors.inspectedOn && inputErrorClass)} /></Field>
        <Field label="Inspected by" htmlFor="fa-inspector"><input id="fa-inspector" type="text" maxLength={100} value={form.inspectedByName} onChange={set('inspectedByName')} className={inputClass} /></Field>
        <Field label="Notes" htmlFor="fa-notes" className="sm:col-span-2"><input id="fa-notes" type="text" maxLength={500} value={form.notes} onChange={set('notes')} className={inputClass} /></Field>
      </div>

      <section className="rounded-lg border border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Characteristics of the drawing · {lines.length}</h4>
          {product && (
            <button type="button" onClick={() => copyPlan.mutate()} disabled={copyPlan.isPending} className="text-xs font-semibold text-indigo-600 hover:text-indigo-500">
              Copy from the inspection plan
            </button>
          )}
        </div>
        <ul className="divide-y divide-gray-100">
          {lines.map((line, index) => {
            const judged = within(line);
            const verdict = judged !== null ? judged : line.passed === '' ? null : line.passed === 'pass';
            return (
              <li key={index} className="space-y-1 px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-6 text-sm text-gray-500">{index + 1}</span>
                  <input type="text" maxLength={120} value={line.characteristic} onChange={(event) => setLine(index, { characteristic: event.target.value })} placeholder="Characteristic" aria-label={`Characteristic ${index + 1}`} className={cn(inputClass, 'min-w-[10rem] flex-1 py-1', errors[`line-${index}`] && inputErrorClass)} />
                  <select value={line.type} onChange={(event) => setLine(index, { type: event.target.value as LineState['type'] })} aria-label={`Kind of characteristic ${index + 1}`} className={cn(inputClass, 'w-32 py-1')}>
                    <option value="measurement">Measured</option>
                    <option value="attribute">Pass / fail</option>
                  </select>
                  {line.type === 'measurement' && (
                    <>
                      <input type="text" maxLength={20} value={line.unit} onChange={(event) => setLine(index, { unit: event.target.value })} placeholder="Unit" aria-label={`Unit of characteristic ${index + 1}`} className={cn(inputClass, 'w-16 py-1')} />
                      <input type="number" step="any" value={line.lowerLimit} onChange={(event) => setLine(index, { lowerLimit: event.target.value })} placeholder="Min" aria-label={`Lower limit of characteristic ${index + 1}`} className={cn(inputClass, 'w-24', small)} />
                      <input type="number" step="any" value={line.upperLimit} onChange={(event) => setLine(index, { upperLimit: event.target.value })} placeholder="Max" aria-label={`Upper limit of characteristic ${index + 1}`} className={cn(inputClass, 'w-24', small)} />
                      <input type="number" step="any" value={line.measuredValue} onChange={(event) => setLine(index, { measuredValue: event.target.value })} placeholder="Measured" aria-label={`Measured value of characteristic ${index + 1}`} className={cn(inputClass, 'w-28', small, judged === false && inputErrorClass)} />
                    </>
                  )}
                  {judged === null && (
                    <select value={line.passed} onChange={(event) => setLine(index, { passed: event.target.value as LineState['passed'] })} aria-label={`Result of characteristic ${index + 1}`} className={cn(inputClass, 'w-32 py-1')}>
                      <option value="">Not checked</option>
                      <option value="pass">Pass</option>
                      <option value="fail">Fail</option>
                    </select>
                  )}
                  <select value={line.instrumentId} onChange={(event) => setLine(index, { instrumentId: event.target.value })} aria-label={`Instrument for characteristic ${index + 1}`} className={cn(inputClass, 'w-40 py-1')}>
                    <option value="">Instrument…</option>
                    {instruments.map((instrument) => (<option key={instrument.id} value={instrument.id}>{instrument.code} · {instrument.name}</option>))}
                  </select>
                  <span className="w-12 text-center">{verdict !== null && <span className={cn(badgeClass, verdict ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-red-50 text-red-700 ring-red-600/20')}>{verdict ? 'Pass' : 'Fail'}</span>}</span>
                  <button type="button" onClick={() => setLines((prev) => prev.filter((_, position) => position !== index))} aria-label={`Remove characteristic ${index + 1}`} className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                </div>
                {errors[`line-${index}`] && <p className="pl-8 text-sm text-red-600">{errors[`line-${index}`]}</p>}
              </li>
            );
          })}
        </ul>
        <div className="flex items-center gap-3 border-t border-gray-200 p-3">
          <button type="button" onClick={() => setLines((prev) => [...prev, emptyLine()])} className={secondaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
            Add Characteristic
          </button>
          {errors.lines && <span className="text-sm text-red-600">{errors.lines}</span>}
        </div>
      </section>

      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>{isPending ? 'Saving…' : fai ? 'Save First Article' : 'Start First Article'}</button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- one first article, and the decision on it

const FaiPanel: React.FC<{ faiId: string; canCreate: boolean; canDecide: boolean; canDelete: boolean; onEdit: (fai: Fai) => void; onClose: () => void; onChanged: () => void }> = ({ faiId, canCreate, canDecide, canDelete, onEdit, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: fai, isLoading, isError, error } = useQuery({ queryKey: ['first-articles', 'one', faiId], queryFn: () => faiApi.one(faiId) });
  const [mode, setMode] = useState<'view' | 'approve' | 'reject'>('view');
  const [note, setNote] = useState('');
  const fail = (actionError: unknown) => toast.error(getApiErrorMessage(actionError, 'That could not be done'), { duration: 7000 });
  const decide = useMutation({
    mutationFn: ({ action, data }: { action: 'approve' | 'reject'; data: object }) => faiApi.act(faiId, action, data),
    onSuccess: (saved) => {
      toast.success(`${saved.faiNumber} ${saved.status === 'rejected' ? 'rejected' : saved.status === 'approved_with_deviation' ? 'approved with deviation' : 'approved'}`);
      queryClient.setQueryData(['first-articles', 'one', faiId], saved);
      setMode('view');
      onChanged();
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => faiApi.remove(faiId),
    onSuccess: () => {
      toast.success('First article deleted');
      onChanged();
      onClose();
    },
    onError: fail,
  });
  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !fai) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The first article could not be loaded.')}</p>;
  const draft = fai.status === 'draft';
  const unchecked = fai.lines - fai.checked;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-700">
        <span className={cn(badgeClass, STATUS[fai.status].className)}>{STATUS[fai.status].label}</span>
        <span className="font-semibold text-gray-900">{fai.productName} <span className="font-normal text-gray-500">{fai.sku}</span></span>
        {fai.drawingNumber && <span>Drawing {fai.drawingNumber}{fai.revision ? ` rev ${fai.revision}` : ''}</span>}
        <span>{REASON_LABEL[fai.reason] ?? labelOf(fai.reason)}</span>
        <span>Inspected {formatDate(fai.inspectedOn)}{fai.inspectedByName ? ` by ${fai.inspectedByName}` : ''}</span>
        {fai.notes && <span className="text-gray-500">{fai.notes}</span>}
      </div>
      {fai.decisionNote && <p className={cn('rounded-md px-4 py-3 text-sm ring-1', fai.status === 'rejected' ? 'bg-red-50 text-red-800 ring-red-600/20' : 'bg-blue-50 text-blue-800 ring-blue-600/20')}>{fai.status === 'rejected' ? 'Rejected: ' : 'Approved on a concession: '}{fai.decisionNote}{fai.decidedBy ? ` — ${fai.decidedBy}, ${formatDate(fai.decidedAt)}` : ''}</p>}
      <p className="text-sm text-gray-700">{fai.checked} of {fai.lines} characteristic(s) checked{fai.failed > 0 ? <span className="font-medium text-red-600"> · {fai.failed} out</span> : fai.checked === fai.lines ? <span className="text-green-700"> · all within</span> : null}</p>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={thClass}>#</th>
              <th scope="col" className={thClass}>Characteristic</th>
              <th scope="col" className={thClass}>Requirement</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Measured</th>
              <th scope="col" className={thClass}>Instrument</th>
              <th scope="col" className={thClass}>Result</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {fai.characteristics.map((line) => (
              <tr key={line.id}>
                <td className="px-4 py-2 text-sm text-gray-500">{line.sequence}</td>
                <td className="px-4 py-2 text-sm font-medium text-gray-900">{line.characteristic}</td>
                <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-600">{limitsOf(line)}</td>
                <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-900 tabular-nums">{line.measuredValue === null ? '—' : `${formatNumber(line.measuredValue, 5)} ${line.unit ?? ''}`}</td>
                <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-600">{line.instrumentCode || '—'}</td>
                <td className="whitespace-nowrap px-4 py-2">{line.passed === null ? <span className="text-sm text-gray-400">Not checked</span> : <span className={cn(badgeClass, line.passed ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-red-50 text-red-700 ring-red-600/20')}>{line.passed ? 'Pass' : 'Fail'}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {mode !== 'view' && (
        <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
          {mode === 'approve' ? (
            <Field label={`${fai.failed} characteristic(s) are out. On what concession is it approved?`} htmlFor="fa-note" required>
              <input id="fa-note" type="text" maxLength={500} autoFocus value={note} onChange={(event) => setNote(event.target.value)} placeholder="The customer's deviation number, or who agreed" className={inputClass} />
            </Field>
          ) : (
            <Field label="Why is the first article rejected?" htmlFor="fa-note" required>
              <input id="fa-note" type="text" maxLength={500} autoFocus value={note} onChange={(event) => setNote(event.target.value)} className={inputClass} />
            </Field>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setMode('view')} className={secondaryButtonClass}>Back</button>
            <button type="button" disabled={decide.isPending || note.trim().length < 3} onClick={() => decide.mutate(mode === 'approve' ? { action: 'approve', data: { deviationNote: note.trim() } } : { action: 'reject', data: { reason: note.trim() } })} className={mode === 'approve' ? primaryButtonClass : dangerButtonClass}>
              {mode === 'approve' ? 'Approve with Deviation' : 'Confirm Rejection'}
            </button>
          </div>
        </div>
      )}
      {mode === 'view' && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 pt-3">
          {draft && unchecked > 0 && <span className="mr-auto text-xs text-gray-500">{unchecked} characteristic(s) still to check before it can be approved</span>}
          <button type="button" onClick={onClose} className={secondaryButtonClass}>Close</button>
          {draft && canDelete && <button type="button" onClick={() => remove.mutate()} disabled={remove.isPending} className={secondaryButtonClass}>Delete</button>}
          {draft && canCreate && <button type="button" onClick={() => onEdit(fai)} className={secondaryButtonClass}>Edit</button>}
          {draft && canDecide && (
            <>
              <button type="button" onClick={() => { setNote(''); setMode('reject'); }} className={secondaryButtonClass}>Reject</button>
              <button type="button" disabled={decide.isPending || unchecked > 0} onClick={() => (fai.failed > 0 ? (setNote(''), setMode('approve')) : decide.mutate({ action: 'approve', data: {} }))} className={primaryButtonClass}>Approve</button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- the tab

export const FirstArticles: React.FC<{ canCreate: boolean; canDecide: boolean; canDelete: boolean; onChanged: () => void }> = ({ canCreate, canDecide, canDelete, onChanged }) => {
  const queryClient = useQueryClient();
  const list = useListState();
  const [status, setStatus] = useState<Status | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Fai | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['first-articles', 'page', { page: list.page, limit: list.limit, search: list.search, status }],
    queryFn: () => faiApi.page({ page: list.page, limit: list.limit, search: list.search, status }),
    placeholderData: keepPreviousData,
  });
  const { data: viewing } = useQuery({ queryKey: ['first-articles', 'one', viewingId], queryFn: () => faiApi.one(viewingId as string), enabled: !!viewingId });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['first-articles', 'page'] });
    onChanged();
  };
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };
  const save = useMutation({
    mutationFn: (input: object) => faiApi.save(editing?.id ?? null, input),
    onSuccess: (fai) => {
      toast.success(editing ? `${fai.faiNumber} saved` : `${fai.faiNumber} started`);
      queryClient.setQueryData(['first-articles', 'one', fai.id], fai);
      refresh();
      closeForm();
      setViewingId(fai.id);
    },
    onError: (saveError) => toast.error(getApiErrorMessage(saveError, 'The first article could not be saved'), { duration: 7000 }),
  });
  const rows = data?.data ?? [];

  return (
    <>
      <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input type="search" value={list.searchInput} onChange={(event) => list.setSearchInput(event.target.value)} placeholder="Search by number, part, drawing or work order" aria-label="Search first articles" className={cn(inputClass, 'pl-9')} />
        </div>
        <select value={status} onChange={(event) => { setStatus(event.target.value as Status | ''); list.setPage(1); }} aria-label="First article status" className={cn(inputClass, 'lg:w-56')}>
          <option value="">All</option>
          {(Object.keys(STATUS) as Status[]).map((value) => (<option key={value} value={value}>{STATUS[value].label}</option>))}
        </select>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            New First Article
          </button>
        )}
      </div>
      <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={thClass}>First article</th>
              <th scope="col" className={thClass}>Part</th>
              <th scope="col" className={thClass}>Drawing</th>
              <th scope="col" className={thClass}>Why</th>
              <th scope="col" className={thClass}>Checked</th>
              <th scope="col" className={thClass}>Status</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isError ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The first articles could not be loaded.')}</td></tr>
            ) : isLoading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <p className="text-sm font-medium text-gray-900">{list.search || status ? 'No first article matches' : 'No first articles yet'}</p>
                  <p className="mt-1 text-sm text-gray-500">Inspect the first piece of a new or changed part against every characteristic of its drawing.</p>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-4 py-3">
                    <button type="button" onClick={() => setViewingId(row.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.faiNumber}</button>
                    <p className="mt-0.5 text-xs text-gray-500">{formatDate(row.inspectedOn)}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{row.productName}<span className="block text-xs text-gray-500">{row.sku}</span></td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{row.drawingNumber || '—'}{row.revision && <span className="text-gray-500"> rev {row.revision}</span>}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{REASON_LABEL[row.reason] ?? labelOf(row.reason)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 tabular-nums">{row.checked} of {row.lines}{row.failed > 0 && <span className="block text-xs font-medium text-red-600">{row.failed} out</span>}</td>
                  <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, STATUS[row.status].className)}>{STATUS[row.status].label}</span></td>
                  <td className="whitespace-nowrap px-4 py-3 text-right"><button type="button" onClick={() => setViewingId(row.id)} className={openButtonClass} aria-label={`Open ${row.faiNumber}`}>Open</button></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <TableFooter page={list.page} limit={list.limit} total={data?.total ?? 0} totalPages={data?.totalPages ?? 1} onPage={list.setPage} onLimit={list.setLimit} />

      <Modal isOpen={formOpen || !!viewingId} onClose={() => (formOpen ? closeForm() : setViewingId(null))} title={formOpen ? (editing ? `Edit ${editing.faiNumber}` : 'New First Article Inspection') : viewing?.faiNumber ?? 'First Article'} size="2xl">
        {formOpen ? (
          <FaiForm key={editing?.id ?? 'new'} fai={editing} isPending={save.isPending} onCancel={closeForm} onSubmit={(input) => save.mutate(input)} />
        ) : (
          viewingId && <FaiPanel key={viewingId} faiId={viewingId} canCreate={canCreate} canDecide={canDecide} canDelete={canDelete} onEdit={(fai) => { setEditing(fai); setFormOpen(true); }} onClose={() => setViewingId(null)} onChanged={refresh} />
        )}
      </Modal>
    </>
  );
};
